import React from 'react';
import { formatHours } from '../../utils/formatHours';

/**
 * PackageLaborChart
 * Custom horizontal bar chart layout — NOT a Syncfusion chart.
 * Each package renders as a card with field/shop bars, a budget line marker,
 * colored left borders per health status, and clickable card behavior.
 * Props: rows, mode, onPackageSelect, selectedPackageId
 */
const PackageLaborChart = ({
  rows,
  mode,
  onPackageSelect,
  selectedPackageId,
}) => {
  // Scale all bars relative to the largest value across all (not just filtered)
  const maxHours =
    Math.max(
      ...rows.map((p) =>
        Math.max(
          p.field_hrs_budgeted || 0,
          p.field_hrs_alltime || 0,
          p.shop_hrs_budgeted || 0,
          p.shop_hrs_alltime || 0
        )
      ),
      100
    ) * 1.1;

  const barH = 22;

  const borderColor = (pkg) => {
    if (pkg.healthStatus === 'over') return '#ef4444';
    if (pkg.healthStatus === 'approaching') return '#f59e0b';
    return '#0f766e';
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        padding: 16,
      }}
    >
      {/* ── Package cards ── */}
      {rows.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 0',
            color: '#94a3b8',
            fontSize: 13,
          }}
        >
          No packages match the current filters
        </div>
      )}

      {rows.map((pkg) => {
        const isSelected = selectedPackageId === pkg.package_id;
        const titleParts = [];
        if (pkg.autodesk_name && String(pkg.autodesk_name).trim() !== '') {
          titleParts.push(pkg.autodesk_name);
        }
        if (pkg.description && String(pkg.description).trim() !== '') {
          titleParts.push(pkg.description);
        }
        const packageTitle =
          titleParts.length > 0 ? titleParts.join(' - ') : 'Unnamed Package';
        return (
          <div
            key={pkg.package_id}
            onClick={() => onPackageSelect(pkg)}
            style={{
              marginBottom: 16,
              cursor: 'pointer',
              padding: '10px 14px',
              borderLeft: `4px solid ${isSelected ? '#3b82f6' : borderColor(pkg)}`,
              backgroundColor: isSelected ? '#eff6ff' : '#fff',
              borderRadius: '0 8px 8px 0',
              boxShadow: isSelected
                ? '0 0 0 1px #3b82f6'
                : '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'box-shadow 0.15s, background 0.15s',
            }}
          >
            {/* ── Card header ── */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 8,
                gap: 8,
              }}
            >
              {/* Left: name, description, area, status */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  flexWrap: 'wrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}
                >
                  {packageTitle}
                </span>
                {pkg.area_name && (
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {pkg.area_name}
                  </span>
                )}
                {pkg.tracking_status_label && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: pkg.tracking_status_color || '#64748b',
                      border: `1px solid ${pkg.tracking_status_color || '#e2e8f0'}`,
                      borderRadius: 20,
                      padding: '1px 7px',
                    }}
                  >
                    {pkg.tracking_status_label}
                  </span>
                )}
                {pkg.healthStatus === 'over' && (
                  <span
                    style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}
                  >
                    ⚠ Over Budget
                  </span>
                )}
              </div>

              {/* Right: combined hrs + this week + % badge */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div
                  style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}
                >
                  {formatHours(pkg.combinedAlltime)}{' '}
                  <span
                    style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}
                  >
                    combined
                  </span>
                </div>
                {mode === 'week' && pkg.weekHrsTotal > 0 && (
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    +{formatHours(pkg.weekHrsTotal)} this week
                  </div>
                )}
                {pkg.budgetPctCombined !== null && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color:
                        pkg.healthStatus === 'over'
                          ? '#dc2626'
                          : pkg.healthStatus === 'approaching'
                            ? '#d97706'
                            : '#15803d',
                    }}
                  >
                    {pkg.budgetPctCombined}%
                  </span>
                )}
              </div>
            </div>

            {/* ── Bar rows: Field + Shop ── */}
            {[
              [
                'Field',
                pkg.field_hrs_alltime || 0,
                pkg.field_hrs_budgeted,
                '#3b82f6',
              ],
              [
                'Shop',
                pkg.shop_hrs_alltime || 0,
                pkg.shop_hrs_budgeted,
                '#8b5cf6',
              ],
            ].map(([label, actual, budget, color]) =>
              actual > 0 || budget ? (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: '#64748b',
                      width: 36,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                  >
                    {label}
                  </span>
                  <div style={{ flex: 1, position: 'relative', height: barH }}>
                    {/* Budget track (gray background at budget width) */}
                    {budget > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `${(budget / maxHours) * 100}%`,
                          height: barH,
                          backgroundColor: '#e2e8f0',
                          borderRadius: 4,
                        }}
                      />
                    )}
                    {/* Actual hours bar */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 0,
                        width: `${(actual / maxHours) * 100}%`,
                        height: barH - 8,
                        backgroundColor:
                          actual > (budget || Infinity) ? '#ef4444' : color,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 6,
                        transition: 'width 0.3s ease',
                      }}
                    >
                      {actual > 30 && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#fff',
                          }}
                        >
                          {formatHours(actual)}
                        </span>
                      )}
                    </div>
                    {/* Budget line marker */}
                    {budget > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: `${(budget / maxHours) * 100}%`,
                          width: 2,
                          height: barH,
                          backgroundColor: '#475569',
                          transform: 'translateX(-1px)',
                        }}
                      />
                    )}
                  </div>
                  {budget > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        width: 50,
                        flexShrink: 0,
                      }}
                    >
                      /{budget}h
                    </span>
                  )}
                </div>
              ) : null
            )}

            <div style={{ borderBottom: '1px solid #f1f5f9', marginTop: 8 }} />
          </div>
        );
      })}

      {/* ── Legend ── */}
      {rows.length > 0 && (
        <div
          style={{ display: 'flex', gap: 20, marginTop: 4, paddingLeft: 46 }}
        >
          {[
            ['#3b82f6', 'Field Actual'],
            ['#8b5cf6', 'Shop Actual'],
            ['#e2e8f0', 'Budget'],
            ['#475569', 'Budget Line'],
          ].map(([color, label]) => (
            <div
              key={label}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: color,
                  borderRadius: 2,
                }}
              />
              <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackageLaborChart;
